import { detectRuntimeEnvironment } from '../helpers/environment.js';
import { CompatibilityMatrix } from '../types/registry.js';
import { Capability } from './capability.js';
import { registry } from './registry.js';

/**
 * ✅ STATIC COMPATIBILITY DATABASE
 * All cross-package compatibility information hardcoded here
 */

// ✅ Wallet Compatibility Matrices
export const WALLET_COMPATIBILITY: Record<string, CompatibilityMatrix> = {
  'ethers@1.0.0': {
    adapterName: 'ethers',
    version: '1.0.0',
    compatibleVersions: ['1.0.0'],
    breakingChanges: [],
    crossModuleCompatibility: [
      {
        moduleName: 'smart-contract',
        // ✅ This wallet can work with any smart-contract adapter that can generate contracts.
        requiresCapabilities: [Capability.ContractGenerator]
      },
      {
        moduleName: 'crosschain',
        // ✅ This wallet can work with any crosschain adapter that can execute operations.
        requiresCapabilities: [Capability.OperationExecutor]
      }
    ]
  },
  'web3auth@1.0.0': {
    adapterName: 'web3auth',
    version: '1.0.0',
    compatibleVersions: ['1.0.0'],
    breakingChanges: [],
    crossModuleCompatibility: [
      {
        moduleName: 'smart-contract',
        // ✅ This wallet can work with any smart-contract adapter that can generate contracts.
        requiresCapabilities: [Capability.ContractGenerator]
      },
      {
        moduleName: 'crosschain',
        // ✅ This wallet can work with any crosschain adapter that can execute operations.
        requiresCapabilities: [Capability.OperationExecutor]
      }
    ]
  }
};

// ✅ Smart Contract Compatibility Matrices
export const SMART_CONTRACT_COMPATIBILITY: Record<string, CompatibilityMatrix> = {
  'openZeppelin@1.0.0': {
    adapterName: 'openZeppelin',
    version: '1.0.0',
    compatibleVersions: ['1.0.0'],
    breakingChanges: [],
    crossModuleCompatibility: [
      {
        moduleName: 'wallet',
        // ✅ This smart-contract adapter needs a wallet that can handle transactions.
        requiresCapabilities: [Capability.TransactionHandler, Capability.RPCHandler]
      }
    ]
  }
};

// ✅ Crosschain Compatibility Matrices  
export const CROSSCHAIN_COMPATIBILITY: Record<string, CompatibilityMatrix> = {
  'lifi@1.0.0': {
    adapterName: 'lifi',
    version: '1.0.0',
    compatibleVersions: ['1.0.0'],
    breakingChanges: [],
    crossModuleCompatibility: [
      {
        moduleName: 'wallet',
        // ✅ This crosschain adapter needs a wallet that can handle transactions and RPC calls.
        requiresCapabilities: [Capability.TransactionHandler, Capability.RPCHandler]
      }
    ]
  }
};

// ✅ Master lookup function
export function getStaticCompatibilityMatrix(
  moduleName: string,
  adapterName: string,
  version: string
): CompatibilityMatrix | undefined {
  const key = `${adapterName}@${version}`;

  switch (moduleName) {
    case 'wallet':
      return WALLET_COMPATIBILITY[key];
    case 'smart-contract':
      return SMART_CONTRACT_COMPATIBILITY[key];
    case 'crosschain':
      return CROSSCHAIN_COMPATIBILITY[key];
    default:
      return undefined;
  }
}

// ✅ Compatibility checker function
export function checkCrossPackageCompatibility(
  sourceModule: string, sourceAdapter: string, sourceVersion: string,
  targetModule: string, targetAdapter: string, targetVersion: string
): boolean {
  const sourceMatrix = getStaticCompatibilityMatrix(sourceModule, sourceAdapter, sourceVersion);
  if (!sourceMatrix) return false;

  // Find the compatibility rule for the target module (e.g., 'wallet')
  const targetModuleCompatRule = sourceMatrix.crossModuleCompatibility.find(
    cmc => cmc.moduleName === targetModule
  );

  if (!targetModuleCompatRule) return false; // No rule defined for this pairing

   // Get the metadata for the actual target adapter instance
  const targetAdapterInfo = registry.getAdapter(targetModule, targetAdapter, targetVersion);
  if (!targetAdapterInfo?.capabilities) return false; // Target adapter not found or has no capabilities

  // ✅ --- REVISED ENVIRONMENT CHECK ---
  const sourceAdapterInfo = registry.getAdapter(sourceModule, sourceAdapter, sourceVersion);
  const currentEnvironments = detectRuntimeEnvironment();

  // 1. Check if the source adapter can run in the current environment.
  if (sourceAdapterInfo?.environment && !sourceAdapterInfo.environment.supportedEnvironments.some(env => currentEnvironments.includes(env))) {
    return false; // Source adapter cannot run here.
  }

  // 2. Check if the target adapter can run in the current environment.
  if (targetAdapterInfo.environment && !targetAdapterInfo.environment.supportedEnvironments.some(env => currentEnvironments.includes(env))) {
    return false; // Target adapter cannot run here.
  }
  // ✅ --- END REVISED CHECK ---

  // ✅ CORE LOGIC: Check if the target adapter's capabilities include ALL required capabilities.
  return targetModuleCompatRule.requiresCapabilities.every(requiredCap =>
    targetAdapterInfo.capabilities.includes(requiredCap)
  );
}